import { useEffect, useState } from "react"
import { Dropdown } from "components/ui/dropdown/Dropdown"
import { ButtonBurger } from "components/ui/buttons/ButtonBurger/ButtonBurger"
import { CloseIcon } from "components/ui/icons/close/CloseIcon"
import { ControllICon } from "components/ui/icons/quotations/viewer/ControlIcon"
import { RulerIcon } from "components/ui/icons/quotations/viewer/RulerIcon"
import { SettingsIcon } from "components/ui/icons/quotations/viewer/SettingsIcon"
import type { MenuProps } from "antd"
import type { WebViewerInstance } from "@pdftron/webviewer"
// import { AIAgentViewerIcon } from "components/ui/icons/iaAgent/AIAgentViewerIcon"

type Props = {
	instance: WebViewerInstance
	onClose?: () => void
	onShareFeedback?: () => void
	onSettings?: () => void
}

export const CustomWebViewerHeader = ({ instance, onClose, onShareFeedback, onSettings }: Props) => {
	const { UI, Core } = instance
	const [isDropdownOpen, setIsDropdownOpen] = useState(false)
	const [isViewControlsOpen, setIsViewControlsOpen] = useState(false)
	const [isRulerActive, setIsRulerActive] = useState(false)

	// Keep the ruler button in sync with the real tool mode. The header is re-rooted
	// (local state resets to false) on Apryse re-renders, while the actual tool mode
	// persists — so derive the button state from the viewer rather than local state.
	useEffect(() => {
		const documentViewer = Core.documentViewer
		const measurementTool = documentViewer.getTool(Core.Tools.ToolNames.DISTANCE_MEASUREMENT)
		const syncFromTool = () => setIsRulerActive(documentViewer.getToolMode() === measurementTool)

		syncFromTool()
		documentViewer.addEventListener("toolModeUpdated", syncFromTool)
		return () => documentViewer.removeEventListener("toolModeUpdated", syncFromTool)
	}, [Core])

	const menuItems: MenuProps["items"] = [
		{
			key: "download",
			label: "Download",
			onClick: () => UI.downloadPdf(),
		},
		{
			key: "saveas",
			label: "Save As",
			onClick: () => UI.downloadPdf(),
		},
		{
			key: "print",
			label: "Print",
			onClick: () => UI.print(),
		},
	]

	const viewControlItems: MenuProps["items"] = [
		{
			key: "single-page",
			label: "Single Page",
			onClick: () => UI.setLayoutMode(UI.LayoutMode.Single),
		},
		{
			key: "continuous",
			label: "Continuous",
			onClick: () => UI.setLayoutMode(UI.LayoutMode.Continuous),
		},
		{
			key: "facing",
			label: "Facing",
			onClick: () => UI.setLayoutMode(UI.LayoutMode.Facing),
		},
		{
			key: "facing-continuous",
			label: "Facing Continuous",
			onClick: () => UI.setLayoutMode(UI.LayoutMode.FacingContinuous),
		},
	]

	const handleAddClick = () => {
		console.log("Add button clicked")
	}

	const handleRulerClick = () => {
		const documentViewer = Core.documentViewer
		const measurementTool = documentViewer.getTool(Core.Tools.ToolNames.DISTANCE_MEASUREMENT)
		const isActive = documentViewer.getToolMode() === measurementTool
		// Toggle the real tool; the toolModeUpdated listener drives isRulerActive.
		documentViewer.setToolMode(
			isActive ? documentViewer.getTool(Core.Tools.ToolNames.EDIT) : measurementTool,
		)
	}

	return (
		<div className="wv-header">
			<div className="wv-header__group">
				<Dropdown
					items={menuItems}
					placement="bottomLeft"
					overlayStyle={{ zIndex: 1500 }}
					open={isDropdownOpen}
					onOpenChange={setIsDropdownOpen}
				>
					<span>
						<ButtonBurger className="wv-btn-tools" />
					</span>
        </Dropdown>
        <Dropdown
					items={viewControlItems}
					placement="bottomLeft"
					overlayStyle={{ zIndex: 1500 }}
					open={isViewControlsOpen}
					onOpenChange={setIsViewControlsOpen}
				>
					<button className="wv-btn-tools wv-btn-icon" onClick={() => setIsViewControlsOpen(!isViewControlsOpen)}>
						<ControllICon />
					</button>
				</Dropdown>
				<button className="wv-btn-tools wv-btn-add" onClick={handleAddClick}>
					+ Add
				</button>
				<button className={`wv-btn-tools wv-btn-ruler${isRulerActive ? " wv-btn-ruler--active" : ""}`} onClick={handleRulerClick}>
					<RulerIcon />
				</button>
			</div>

			<div className="wv-header__group">
				<button className="wv-btn-share-feedback" onClick={onShareFeedback}>
					Share feedback
				</button>
				{/* <button className="wv-btn-ai-agent"><AIAgentViewerIcon /> AI Agent</button> */}
				{/* <button className="wv-btn-generate-price">Generate price</button> */}
				<button
					className="wv-btn-tools wv-btn-icon wv-btn-settings"
					onClick={onSettings}
					aria-label="Quotation settings"
				>
					<SettingsIcon />
				</button>
				<button className="wv-btn-close" onClick={onClose}>
					<CloseIcon />
				</button>
			</div>
		</div>
	)
}
